import React, { useMemo, useState } from "react"
import ReactApexChart from "react-apexcharts"
import moment from "moment"
import { getSameWeekNextYear, getSameWeekPreviousYear, getWeekTooltipLabel } from "utils/dateUtil"

// ── Sort Order cycling button ────────────────────────────────────────────────
const SORT_STATES = [
  { key: "chronological", icon: "⇅", label: "Sort",       color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" },
  { key: "ascending",     icon: "↑", label: "Low → High", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  { key: "descending",    icon: "↓", label: "High → Low", color: "#0f766e", bg: "#f0fdfa", border: "#99f6e4" },
]

const SortButton = ({ sortOrder, onToggle }) => {
  const current = SORT_STATES.find(s => s.key === sortOrder) || SORT_STATES[0]
  return (
    <button
      onClick={onToggle}
      title={`Sort Order: ${current.label} — click to change`}
      style={{
        display: "flex", alignItems: "center", gap: "8px",
        background: current.bg, border: `1.5px solid ${current.border}`,
        borderRadius: "12px", padding: "9px 16px",
        cursor: "pointer", outline: "none",
        transition: "all 0.18s ease", whiteSpace: "nowrap",
        userSelect: "none", minWidth: "130px",
      }}
    >
      <span style={{ fontSize: "18px", lineHeight: 1, color: current.color, fontWeight: 700, minWidth: "20px", textAlign: "center" }}>
        {current.icon}
      </span>
      <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <span style={{ fontSize: "9px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", lineHeight: 1.2 }}>
          Sort Order
        </span>
        <span style={{ fontSize: "12px", fontWeight: 700, color: current.color, lineHeight: 1.4 }}>
          {current.label}
        </span>
      </span>
    </button>
  )
}

const GMPBarLineChart = ({
  data,
  previousData,
  closeOut,
  closeOutPreviousYear,
  sortOrder: sortOrderProp,
  onSortOrderChange,
}) => {
  const [localSortOrder, setLocalSortOrder] = useState("chronological")
  const sortOrder = sortOrderProp !== undefined ? sortOrderProp : localSortOrder
  const setSortOrder = onSortOrderChange ?? setLocalSortOrder

  const handleToggle = () => {
    const next =
      sortOrder === "chronological" ? "ascending"
      : sortOrder === "ascending" ? "descending"
      : "chronological"
    setSortOrder(next)
  }

  // ── Union of all ISO weeks from both years ──────────────────────────────
  const allWeeks = useMemo(() => {
    const weekSet = new Set()
    data?.forEach(i => weekSet.add(moment(i.date).isoWeek()))
    previousData?.forEach(i => weekSet.add(moment(i.date).isoWeek()))
    return Array.from(weekSet).sort((a, b) => a - b)
  }, [data, previousData])

  // ── Current year score map ──────────────────────────────────────────────
  const scoreMap = useMemo(() => {
    const map = {}
    ;(data || []).forEach(i => { map[moment(i.date).isoWeek()] = Number(i.score) || 0 })
    return map
  }, [data])

  const dateMap = useMemo(() => {
    const map = {}
    ;(data || []).forEach(i => { map[moment(i.date).isoWeek()] = moment(i.date).format("DD-MM-YYYY") })
    return map
  }, [data])

  const closeOutMap = useMemo(() => new Map(
    (closeOut || []).map(i => [moment(i.date).isoWeek(), Number(i.closeOut)])
  ), [closeOut])

  // ── Previous year score map ─────────────────────────────────────────────
  const prevScoreMap = useMemo(() => {
    const map = {}
    ;(previousData || []).forEach(i => { map[moment(i.date).isoWeek()] = Number(i.score) || 0 })
    return map
  }, [previousData])

  const prevDateMap = useMemo(() => {
    const map = {}
    ;(previousData || []).forEach(i => { map[moment(i.date).isoWeek()] = moment(i.date).format("DD-MM-YYYY") })
    return map
  }, [previousData])

  const prevCloseOutMap = useMemo(() => new Map(
    (closeOutPreviousYear || []).map(i => [moment(i.date).isoWeek(), Number(i.closeOut)])
  ), [closeOutPreviousYear])

  // ── Apply sort order ────────────────────────────────────────────────────
  const displayWeeks = useMemo(() => {
    if (sortOrder === "ascending") {
      return [...allWeeks].sort((a, b) => {
        const vA = scoreMap[a] ?? 0
        const vB = scoreMap[b] ?? 0
        return vA !== vB ? vA - vB : a - b
      })
    }
    if (sortOrder === "descending") {
      return [...allWeeks].sort((a, b) => {
        const vA = scoreMap[a] ?? 0
        const vB = scoreMap[b] ?? 0
        return vA !== vB ? vB - vA : a - b
      })
    }
    return allWeeks
  }, [allWeeks, scoreMap, sortOrder])

  // ── Build series arrays from displayWeeks ───────────────────────────────
  const categories = displayWeeks.map(w => `${w}`)
  const scores     = displayWeeks.map(w => scoreMap[w] !== undefined ? scoreMap[w] : null)
  const closeOuts  = displayWeeks.map(w => closeOutMap.has(w) ? closeOutMap.get(w) : null)
  const prevScores = displayWeeks.map(w => prevScoreMap[w] !== undefined ? prevScoreMap[w] : null)

  // ── Year labels ─────────────────────────────────────────────────────────
  const currentYear  = data?.length ? moment(data[0].date).year() : moment().year()
  const previousYear = previousData?.length ? moment(previousData[0].date).year() : currentYear - 1

  // ── Stats ───────────────────────────────────────────────────────────────
  const validScores = scores.filter(s => s !== null)
  const avg = validScores.length ? (validScores.reduce((a, b) => a + b, 0) / validScores.length).toFixed(1) : 0
  const max = validScores.length ? Math.max(...validScores) : 0
  const min = validScores.length ? Math.min(...validScores) : 0

  // ── Chart config ────────────────────────────────────────────────────────
  const options = {
    chart: {
      type: "bar", height: 320, toolbar: { show: false },
      background: "transparent", foreColor: "#1a1a2e",
      zoom: { enabled: false }, animations: { enabled: true, speed: 400 },
    },
    plotOptions: {
      bar: { columnWidth: allWeeks.length > 60 ? "90%" : "65%", borderRadius: 4, grouped: true },
    },
    dataLabels: { enabled: false },
    stroke: { show: true, width: [0, 0, 2.5], curve: "smooth" },
    markers: { size: [0, 0, 3], strokeWidth: 0 },
    colors: ["#22c55e", "#f97316", "#6366f1"],
    tooltip: {
      shared: true, intersect: false, theme: "light",
      x: {
        formatter: (val, { dataPointIndex }) => {
          const weekNum             = displayWeeks[dataPointIndex]
          const currentDate         = dateMap[weekNum]
          const prevDate            = prevDateMap[weekNum]
          const resolvedCurrentDate = currentDate || (prevDate ? getSameWeekNextYear(prevDate) : null)
          const resolvedPrevDate    = prevDate || (currentDate ? getSameWeekPreviousYear(currentDate) : null)
          const currentLabel        = getWeekTooltipLabel(weekNum - 1, resolvedCurrentDate)
          const prevLabel           = getWeekTooltipLabel(weekNum - 1, resolvedPrevDate)
          return `Current Year : ${currentLabel || "N/A"}<br/> Previous Year : ${prevLabel || "N/A"}`
        },
      },
      y: { formatter: val => val !== null && val !== undefined ? `${val}%` : "N/A" },
    },
    xaxis: {
      categories,
      overwriteCategories: categories,
      labels: {
        rotate: 0,
        style: { colors: "#1a1a2e", fontSize: allWeeks.length > 60 ? "9px" : "11px", fontWeight: 600 },
      },
    },
    yaxis: {
      min: 0, max: 100,
      title: { text: "GMP Score (%)", style: { color: "#1a1a2e", fontSize: "12px", fontWeight: 700 } },
      labels: { style: { colors: "#1a1a2e", fontSize: "11px", fontWeight: 600 }, formatter: val => `${val}%` },
    },
    grid: {
      borderColor: "#e8e8f0", strokeDashArray: 4,
      xaxis: { lines: { show: false } }, yaxis: { lines: { show: true } },
    },
    legend: { show: false },
  }

  const series = [
    { name: `GMP Score (${currentYear})`,       type: "bar",  data: scores },
    { name: `Close Out Score (${currentYear})`, type: "bar",  data: closeOuts },
    { name: `GMP Score (${previousYear})`,      type: "line", data: prevScores },
  ]

  return (
    <div>
      {/* ── Header ── */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h5 className="mb-0 fw-bold" style={{ color: "#1a1a2e", fontSize: "16px" }}>
          GMP — Good Manufacturing Practice
        </h5>

        <div className="d-flex gap-3 align-items-center flex-wrap">
          {/* Sort Order cycling button */}
          <SortButton sortOrder={sortOrder} onToggle={handleToggle} />

          {/* Stats */}
          <div className="d-flex gap-3">
            {[
              { label: "PEAK",    value: `${max}%`, color: "#22c55e", bg: "#f0fdf4" },
              { label: "AVERAGE", value: `${avg}%`, color: "#6366f1", bg: "#eef0fb" },
              { label: "LOWEST",  value: `${min}%`, color: "#ef4444", bg: "#fef2f2" },
            ].map((stat, i) => (
              <div
                key={i}
                style={{ background: stat.bg, borderRadius: "10px", padding: "10px 14px", borderLeft: `4px solid ${stat.color}`, minWidth: "90px", cursor: "pointer" }}
              >
                <div style={{ fontSize: "12px", fontWeight: 600 }}>{stat.label}</div>
                <div style={{ fontSize: "18px", fontWeight: 800, color: stat.color }}>{stat.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Chart ── */}
      <ReactApexChart options={options} series={series} type="bar" height={300} width="100%" />

      {/* ── Legend ── */}
      <div className="d-flex justify-content-center gap-4 mt-2 flex-wrap" style={{ borderTop: "1px solid #f0f0f0", paddingTop: "12px" }}>
        {[
          { color: "#22c55e", label: `GMP Score (${currentYear})`,       line: false },
          { color: "#f97316", label: `Close Out Score (${currentYear})`, line: false },
          { color: "#6366f1", label: `GMP Score (${previousYear})`,      line: true  },
        ].map((l, i) => (
          <div key={i} className="d-flex align-items-center gap-2">
            {l.line ? (
              <span style={{ display: "inline-flex", alignItems: "center", width: 20 }}>
                <span style={{ flex: 1, height: 2.5, background: l.color, borderRadius: 2 }} />
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: l.color, flexShrink: 0 }} />
                <span style={{ flex: 1, height: 2.5, background: l.color, borderRadius: 2 }} />
              </span>
            ) : (
              <span style={{ width: 12, height: 12, borderRadius: 3, background: l.color, display: "inline-block" }} />
            )}
            <small style={{ color: "#1a1a2e", fontWeight: 600, fontSize: "11px" }}>{l.label}</small>
          </div>
        ))}
      </div>
    </div>
  )
}

export default GMPBarLineChart
